import type { ReactNode } from 'react';
import { cn } from '@/shared/lib/cn';

/**
 * Area under the page header (01-design-system §6.1, 03-modules §1.1):
 *
 *   ┌ subbar (optional, h48 sub-tab bar "Papkalar paneli", starts 1 px under the header line) ┐┌ aside ┐
 *   │ gutter 10 (bg-app) → children (usually a <ContentPanel>)                                 ││ w300/ │
 *   └──────────────────────────────────────────────────────────────────────────────────────────┘└ w400 ┘
 *
 * Content top lands at y86 without and y134 with a sub-tab bar, exactly as in 497:21046 / 577:13896; the aside
 * (right side panel, §2.3) spans the full height under the header like 585:14707.
 */
export function PageLayout({
  subbar,
  aside,
  className,
  children,
}: {
  subbar?: ReactNode;
  aside?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex min-h-0 flex-1">
      <div className="flex min-w-0 flex-1 flex-col">
        {subbar ? <div className="mt-px shrink-0">{subbar}</div> : null}
        <div className={cn('flex min-h-0 flex-1 flex-col p-10', subbar ? 'pt-9' : null, className)}>{children}</div>
      </div>
      {aside}
    </div>
  );
}

/**
 * Content panel: `bg-panel` r12, padding 10, gap 14 (tables, cards — 497:21117). `variant="canvas"` is the
 * dashboard/drawing panel: r4, padding 14 (317:6933). Scrolls internally; the page never scrolls.
 */
export function ContentPanel({
  variant = 'card',
  className,
  children,
}: {
  variant?: 'card' | 'canvas';
  className?: string;
  children: ReactNode;
}) {
  return (
    <section
      className={cn(
        'flex min-h-0 flex-1 flex-col gap-14 overflow-auto bg-panel',
        variant === 'canvas' ? 'rounded-4 p-14' : 'rounded-12 p-10',
        className,
      )}
    >
      {children}
    </section>
  );
}
